





















































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { ReferralChannel, ReferralNodeType, ReferralStatus } from '@/api/types'
import { InputSelectOption } from '@/helpers/options'
import { CompanyAPI, SourceAPI, WebhookAPI } from '@/api/backend-client'
import { Webhook } from '@/api/model/webhook'
import { Company } from '@/api/model/company'
import { Branch } from '@/api/model/branch'
import { ReferralNode } from '@/api/model/referral-node'
import { Source } from '@/api/model/source'
import { Pipeline } from '@/api/model/pipeline'
import { usePipelineConnections } from '@/composables/usePipelineConnections'

const pipelineConnections = usePipelineConnections()

@Component
export default class CompanySettingsWebhookEdit extends Vue {
    @Prop({ required: true }) readonly id!: string
    webhook: Webhook = null
    loading: boolean = false
    availableBranches: Branch[] = []
    availableSources: Source[] = []

    async mounted(): Promise<void> {
        this.scrollToTop()
        await this.loadContent()
    }

    async loadContent(): Promise<void> {
        this.loading = true
        this.webhook = await WebhookAPI.get(this.id)
        await this.reloadAvailableBranches()
        await this.reloadAvailableSources()
        this.loading = false
    }

    scrollToTop(): void {
        document.querySelector('.main').scrollTo({ top: 0 })
    }

    async save(): Promise<void> {
        this.loading = true
        this.scrollToTop()
        await WebhookAPI.update(this.id, this.webhook)
        this.loading = false
        await this.backToLister()
    }

    async backToLister(): Promise<void> {
        await this.$router.push({
            name: 'company_settings_smart_marketing_webhooks',
        })
    }

    @Watch('referralType')
    async reloadAvailableBranches(): Promise<void> {
        this.$currentCompany.ensureOutgoingReferralsAreLoaded()

        if (!this.webhook.referralType) {
            this.availableBranches = []
            return
        }

        this.availableBranches = (await Promise.all(this.$currentCompany.outgoingReferrals

            // Keep only active referral nodes.
            .filter((node: ReferralNode) => node.active === true)

            // Keep only desired referral type nodes.
            .filter((node: ReferralNode) => node.type === this.webhook.referralType as unknown as ReferralNodeType)

            // Create a map of branches.
            .map(async (node: ReferralNode) => {
                const company = await CompanyAPI.get(node.target.id)
                // A company that can't be fetched has no branches to offer
                return (company?.getAvailableBranches() ?? [])

                    // Keep branches that accept the referral type.
                    .filter((branch: Branch) => branch.acceptsReferralType(this.webhook.referralType))

                    // Sort alphabetically.
                    .sort((a: Branch, b: Branch) => a.name.localeCompare(b.name))
            })))

            // Flatten promise.all results.
            .flat() as Branch[]
    }

    @Watch('webhook.assignedToCompany')
    async reloadAvailableSources(): Promise<void> {
        if (this.webhook.assignedToCompany === null) {
            this.availableSources.splice(0)
            return
        }

        const sources = (await SourceAPI.getAll({ company: this.webhook.assignedToCompany.id, active: 1 }))

        this.availableSources.splice(0)
        this.availableSources.push(...sources)
    }

    get assignableCompanies(): Company[] {
        const assignableCompanies = new Map<string, Company>()
        this.availableBranches.forEach((branch: Branch) => {
            assignableCompanies.set(branch.company.id, branch.company)
        })

        if (this.availableBranches.length === 0) {
            assignableCompanies.set(this.$currentCompany.id, this.$currentCompany)
        }

        return Array.from(assignableCompanies.values()).sort((a: Company, b: Company) => a.name.localeCompare(b.name))
    }

    get assignableBranches(): Branch[] {
        if (!this.webhook.assignedToCompany) {
            return []
        }

        const assignableBranches = new Map<string, Branch>()
        this.availableBranches.forEach((branch: Branch) => {
            if (branch.company.id === this.webhook.assignedToCompany.id) {
                assignableBranches.set(branch.id, branch)
            }
        })

        return Array.from(assignableBranches.values())
            .sort((a: Branch, b: Branch) => a.name.localeCompare(b.name))
    }

    get availableStatuses(): InputSelectOption[] {
        if (this.$currentCompany.pipelinesEnabled) {
            return [ReferralStatus.OPEN, ReferralStatus.PROCESSED].map(status => ({
                label: this.$t('referral.phaseModelStatus.' + status).toString(),
                value: status,
            }))
        } else {
            return [ReferralStatus.NEW, ReferralStatus.PROCESSED].map(status => ({
                label: this.$t('referral.status.' + status).toString(),
                value: status,
            }))
        }
    }

    get availableReferralTypes(): InputSelectOption[] {
        return this.$currentCompany.handledReferralTypes
            .map(type => ({
                label: this.$t('referral.type.' + type).toString(),
                value: type,
            }))
            .sort((a, b) => {
                return a.label.localeCompare(b.label)
            })
    }

    get availablePriorities(): InputSelectOption[] {
        return [false, true].map(priority => ({
            label: this.$t('webhook.priorities.' + (priority ? 'yes' : 'no')).toString(),
            value: priority,
        }))
    }

    get availableSourceOptions(): InputSelectOption[] {
        return this.availableSources
            .filter(source => source.channel === ReferralChannel.ONLINE)
            .map(source => ({
                label: source.name,
                value: source.id,
            }))
    }

    get pipelinesEnabled(): boolean {
        return this.webhook.assignedToCompany?.pipelinesEnabled
    }

    get selectedPipeline(): Pipeline|null {
        return this.outgoingPipelines
            .find(pipeline => pipeline.id === this.webhook.pipeline?.id) || null
    }

    get pipeline(): string|null {
        return this.webhook?.pipeline?.id || null
    }

    set pipeline(id: string|null) {
        if (this.outgoingPipelines.length === 0) {
            return
        }

        this.webhook.pipeline = this.outgoingPipelines
            .find(v => v.id === id) || null
    }

    get newSource(): string|null {
        return this.webhook?.newSource?.id || null
    }

    set newSource(id: string|null) {
        this.webhook.newSource = this.availableSources.find(v => v.id === id) || null
    }

    get outgoingPipelines(): Pipeline[] {
        return pipelineConnections
            .buildOutgoingPipelines()
    }

    get pipelineOptions(): InputSelectOption[] {
        return this.outgoingPipelines
            .filter((pipeline: Pipeline) => pipeline.company?.id === this.webhook.assignedToCompany?.id)
            .map(pipeline => ({
                label: pipeline.name,
                value: pipeline.id,
                subtext: pipeline.company?.name,
            }))
    }

    get companyOptions(): Company[] {
        const companies = new Map<string, Company>()

        // First fill map with referral type companies.
        this.assignableCompanies.forEach((company: Company) => {
            companies.set(company.id, company)
        })

        // Then fill with pipeline companies (overwriting referral type companies).
        pipelineConnections.buildOutgoingCompanies().forEach((company: Company) => {
            companies.set(company.id, company)
        })

        return Array.from(companies.values())
            .sort((a: Company, b: Company) => a.name.localeCompare(b.name))
    }

    get branchOptions(): Branch[] {

        // legacy support
        if (this.pipelinesEnabled === false) {
            return this.assignableBranches
        }

        if (this.webhook.pipeline) {
            if (this.selectedPipeline) {
                return this.selectedPipeline.branches
                    .sort((a: Branch, b: Branch) => a.name.localeCompare(b.name))
            }
        }

        return []
    }
}
